import React, { useCallback, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";

import { currentUserService } from "../../services/user/CurrentUserStorage";
import { useHistory } from "react-router-dom";
import { PubSubEvents } from "../../services/PubSubEvents";
import { organizationSubscriptionService } from "../../services/organization/OrganizationSubscription";
import { lastAccessedOrganizationService } from "../../services/organization/LastAccessedOrganizationStorage";
import { uploadFilesService, UploadStatusOption } from "../../services/upload/UploadFilesService";
import { OrganizationSelectorModel } from "./OrganizationSelectorModel";
import { UploadInProgressWarningModel } from "./UploadInProgressWarningModel";
import { browser } from "../../services/browser-info/BrowserService";
import UploadInProgressWarning from "./UploadInProgressWarning";
import { warningLeavePageService } from "../../services/page/WarningLeavePageService";
import { DemoTag, EnterpriseTag } from "../grid-tag/Tags";
import { Dropdown, DropdownOption } from "../dropdown/Dropdown";

export default observer(function OrganizationSelector() {
  const history = useHistory();

  const [organizationSelector] = useState(() =>
    OrganizationSelectorModel.create(
      { organizationId: currentUserService.currentUser.currentOrganization?.id },
      {
        lastAccessedOrganizationService,
        currentUserService,
        organizationSubscriptionService,
      }
    )
  );

  const [uploadInProgressWarning] = useState(() => UploadInProgressWarningModel.create({}, {}));

  const redirectToDashboard = useCallback(() => {
    history.push("/");
  }, [history]);

  const uploadIsOngoing = useCallback(() => {
    return uploadFilesService.uploadStatus === UploadStatusOption.ongoing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFilesService.uploadStatus]);

  const handleChangeOrganizationWhenPageIsDirty = useCallback(() => {
    redirectToDashboard();

    PubSub.subscribe(PubSubEvents.ConfirmLeavePage, () => {
      organizationSelector.updateApplicationWithNewOrganization();
    });

    PubSub.subscribe(PubSubEvents.CancelLeavePage, () => {
      PubSub.unsubscribe(PubSubEvents.ConfirmLeavePage);
    });
  }, [organizationSelector, redirectToDashboard]);

  const handleChangeOrganization = useCallback(() => {
    redirectToDashboard();
    organizationSelector.updateApplicationWithNewOrganization();
  }, [organizationSelector, redirectToDashboard]);

  const handleChangeOrganizationAtUpload = useCallback(() => {
    uploadFilesService.cancelUpload();
    redirectToDashboard();
    organizationSelector.updateApplicationWithNewOrganization();
    browser.refresh(); //This is a workaround until the behavior will be handled properly with a new story
  }, [organizationSelector, redirectToDashboard]);

  const onChange = useCallback(
    (option: DropdownOption) => {
      const organizationId = Number(option?.id);
      organizationSelector.changeOrganization(organizationId);

      if (uploadIsOngoing()) {
        uploadInProgressWarning.show();
        return;
      }

      if (warningLeavePageService.isDirty) {
        handleChangeOrganizationWhenPageIsDirty();
        return;
      }

      handleChangeOrganization();
    },
    [
      organizationSelector,
      uploadIsOngoing,
      handleChangeOrganization,
      handleChangeOrganizationWhenPageIsDirty,
      uploadInProgressWarning,
    ]
  );

  const organizationTag = useMemo(() => {
    if (currentUserService.currentUser.currentOrganization?.allowEnterprise)
      return <EnterpriseTag />;
    else if (currentUserService.currentUser.currentOrganization?.isDemo) return <DemoTag />;
    else return <></>;
  }, []);

  return (
    <div>
      <div className="selector-container">
        {organizationTag}
        <Dropdown
          selectedOption={{
            id: currentUserService.currentUser.currentOrganization?.id,
            name: currentUserService.currentUser.currentOrganization?.name,
          }}
          options={organizationSelector.organizationNames}
          onChange={onChange}
          onDarkBackground
        />
      </div>
      <UploadInProgressWarning
        uploadInProgressWarning={uploadInProgressWarning}
        proceedMethod={handleChangeOrganizationAtUpload}
      />
    </div>
  );
});
